import { z } from 'zod';

/**
 * Резервные копии компании (АД-18, раздел 6.24): куда делать копии, как часто и сколько хранить.
 * Минимум портала — копия раз в сутки в хранилище портала, 30 суточных копий: компания отключить его не может,
 * только добавить свои места, делать чаще и хранить дольше.
 */
export const BACKUP_PORTAL_KEY = 'portal';
export const BACKUP_PORTAL_MIN_DAILY = 30;
export const BACKUP_MAX_PLACES = 2;

export const BackupFrequency = z.enum(['daily', 'weekly', 'monthly']);
export type BackupFrequency = z.infer<typeof BackupFrequency>;
export const BACKUP_FREQUENCY_TITLES: Record<BackupFrequency, string> = {
  daily: 'Раз в сутки',
  weekly: 'Раз в неделю',
  monthly: 'Раз в месяц',
};

export const BackupSettings = z.object({
  frequency: BackupFrequency,
  /** Время начала копии по часовому поясу компании, «ЧЧ:ММ» */
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  /** День недели для недельной копии: 1 — понедельник … 7 — воскресенье */
  weekday: z.number().int().min(1).max(7),
  /** День месяца для месячной копии (до 28, чтобы был в каждом месяце) */
  monthDay: z.number().int().min(1).max(28),
  keepDaily: z.number().int().min(1).max(400),
  keepWeekly: z.number().int().min(0).max(104),
  keepMonthly: z.number().int().min(0).max(120),
  /** Как часто проверять восстановление, дней (не реже раза в месяц) */
  verifyEveryDays: z.number().int().min(1).max(31),
});
export type BackupSettings = z.infer<typeof BackupSettings>;

export const BACKUP_DEFAULTS: BackupSettings = {
  frequency: 'daily',
  time: '02:00',
  weekday: 1,
  monthDay: 1,
  keepDaily: 30,
  keepWeekly: 8,
  keepMonthly: 12,
  verifyEveryDays: 30,
};

export const BackupCountry = z.enum(['KZ', 'other']);

export const BackupPlaceInput = z.object({
  name: z.string().trim().min(2).max(80),
  endpoint: z
    .string()
    .trim()
    .url()
    .max(300)
    .refine((u) => u.startsWith('https://') || u.startsWith('http://'), 'Адрес должен начинаться с https://'),
  bucket: z.string().trim().min(3).max(120),
  region: z.string().trim().min(1).max(60).default('auto'),
  prefix: z
    .string()
    .trim()
    .max(120)
    .regex(/^[A-Za-z0-9_./-]*$/, 'В каталоге только латинские буквы, цифры и . _ - /')
    .default(''),
  forcePathStyle: z.boolean().default(true),
  country: BackupCountry.default('other'),
  enabled: z.boolean().default(true),
  /** При создании обязательны; при изменении пустые значения оставляют прежний ключ */
  accessKeyId: z.string().trim().max(200).optional(),
  secretAccessKey: z.string().max(400).optional(),
});
export type BackupPlaceInput = z.infer<typeof BackupPlaceInput>;

export const BackupPlaceView = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.literal('s3'),
  endpoint: z.string(),
  bucket: z.string(),
  region: z.string(),
  prefix: z.string(),
  forcePathStyle: z.boolean(),
  country: BackupCountry,
  enabled: z.boolean(),
  /** Ключ доступа задан (сам он в ответах не отдаётся) */
  hasSecret: z.boolean(),
  lastCheckAt: z.string().nullable(),
  lastCheckOk: z.boolean().nullable(),
  lastCheckMessage: z.string().nullable(),
  /** Предупреждение: хранилище не в Казахстане */
  warning: z.string().nullable(),
});
export type BackupPlaceView = z.infer<typeof BackupPlaceView>;

export const BackupRunStatus = z.enum(['queued', 'running', 'ok', 'failed', 'deleted']);
export const BackupRunView = z.object({
  id: z.string(),
  placeKey: z.string(),
  placeName: z.string(),
  kind: BackupFrequency,
  trigger: z.enum(['schedule', 'manual']),
  status: BackupRunStatus,
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  sizeBytes: z.number(),
  sha256: z.string().nullable(),
  tablesCount: z.number(),
  rowsCount: z.number(),
  filesTotal: z.number(),
  filesNew: z.number(),
  error: z.string().nullable(),
  verifyStatus: z.string().nullable(),
  verifiedAt: z.string().nullable(),
});
export type BackupRunView = z.infer<typeof BackupRunView>;

export const BackupsState = z.object({
  settings: BackupSettings,
  /** Что настроено по умолчанию и чего компания отключить не может */
  portal: z.object({
    configured: z.boolean(),
    warning: z.string().nullable(),
    minKeepDaily: z.number(),
    usedBytes: z.number(),
    limitBytes: z.number().nullable(),
  }),
  places: z.array(BackupPlaceView),
  runs: z.array(BackupRunView),
  keyVersion: z.number().nullable(),
  /** Текущий человек — владелец компании: только он скачивает копии и ключ */
  isOwner: z.boolean(),
  /** На сервере портала задан ключ шифрования секретов; без него копии делать нельзя */
  serverKeyReady: z.boolean(),
  isDemo: z.boolean(),
  /** Когда по расписанию следующая копия (для отображения) */
  nextRunAt: z.string().nullable(),
});
export type BackupsState = z.infer<typeof BackupsState>;

export const BackupPasswordInput = z.object({ password: z.string().min(1).max(200) });
export const BackupDownloadView = z.object({ url: z.string(), expiresInSeconds: z.number() });
export const BackupKeyView = z.object({ key: z.string(), version: z.number() });
export const BackupCheckView = z.object({ ok: z.boolean(), message: z.string() });
